import axios from 'axios';
import { flatten, get, isEmpty } from 'lodash';
import { stringify } from 'query-string';

import * as Constants from '../../constants';
import { AXIOS_DEFAULT_OPTIONS } from '../';
import { getValidationErrors } from '../../utils';
import { debouncedAxiosGet, stringifyUrl } from '../../utils/actions';
import { getConfig } from '../../reducers/portalConfig';
import { getOrgPayload } from './requestParser';

const initApplication = (dispatch, isLoading = true) =>
  dispatch({
    type: Constants.APP_LOADING,
    isLoading,
  });

export const resetApp = () => dispatch => dispatch({ type: Constants.APP_RESET });
export const showLoading = isLoading => dispatch => initApplication(dispatch, isLoading);

export const init = (dispatch, isLoading = true) =>
  dispatch({
    type: Constants.APP_LOADING,
    isLoading,
  });

export const fetchAllOrganizations = ({ size = 100 } = {}) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const urlPrefix = `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/internal/organizations`;

    const getCascadingRequests = (totalPages) => {
      const cascadingRequests = [];
      for (let page = 1; page < totalPages; page++) {
        const url = stringifyUrl({
          url: urlPrefix,
          query: { status: Constants.ORGANIZATION_STATUS_ENABLED, page, size },
        });
        cascadingRequests.push(
          axios.get(url, AXIOS_DEFAULT_OPTIONS),
        );
      }
      return cascadingRequests;
    };

    const url = stringifyUrl({
      url: urlPrefix,
      query: { status: Constants.ORGANIZATION_STATUS_ENABLED, page: 0, size },
    });

    await axios.get(url, AXIOS_DEFAULT_OPTIONS)
      .then(async response => {
        const { totalPages } = response.data;
        if (totalPages > 1) {
          const cascadingResponses = await Promise.all(
            getCascadingRequests(totalPages),
          );
          const cascadingResponsesResults = cascadingResponses
            .map(res => res.data.results);
          return {
            ...response.data,
            results: [
              ...response.data.results,
              ...flatten(cascadingResponsesResults),
            ],
          };
        }
        return response.data;
      })
      .then(response =>
        dispatch({
          type: Constants.ORGANIZATIONS_GET_SUCCESS,
          payload: response,
        }),
      )
      .catch(error =>
        dispatch({
          type: Constants.ORGANIZATIONS_GET_ERROR,
          payload: getValidationErrors(error),
        }),
      );
  };

export const fetchOrganizations = ({ name = null, type = Constants.ANY, page = 0, size = 12 }) =>
  async (dispatch, getState) => {
    initApplication(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const params = stringify({
      name: !isEmpty(name) ? name : undefined,
      page,
      size,
      type: (type !== Constants.ANY) ? type : undefined,
      status: 'ENABLED',
    }, true);

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/internal/organizations?${params}`;
    await axios.get(url, { credentials: 'include' })
      .then(response =>
        dispatch({
          type: Constants.ORGANIZATION_LIST_GET_SUCCESS,
          payload: response.data,
        }),
      )
      .catch(() =>
        dispatch({
          type: Constants.ORGANIZATION_LIST_GET_ERROR,
          payload: [''],
        }),
      );
  };

export const fetchOrganization = (organizationUuid) =>
  async (dispatch, getState) => {
    initApplication(dispatch);
    const config = getConfig(getState());
    const { portal } = config;
    const url =
        `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/1.0/organizations/${organizationUuid}`;
    dispatch({
      type: Constants.ORGANIZATION_GET_REQUEST,
    });
    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.ORGANIZATION_GET_SUCCESS,
          payload: response.data,
        });
      })
      .catch(() =>
        dispatch({
          type: Constants.ORGANIZATION_GET_ERROR,
          payload: [''],
        }),
      );
  };

export const fetchAccountPlans = () =>
  async (dispatch, getState) => {
    initApplication(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const params = stringify({
      $select: 'Uuid,Name',
      $inlinecount: 'allpages',
    }, true);

    dispatch({
      type: Constants.ACCOUNTPLANS_GET_REQUEST,
    });
    const url =
        `${portal.hostname}/admin/Portal.svc/AccountPlans?${params}`;

    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.ACCOUNTPLANS_GET_SUCCESS,
          payload: response.data,
        });
      })
      .catch(() =>
        dispatch({
          type: Constants.ACCOUNTPLANS_GET_ERROR,
          payload: [''],
        }),
      );
  };

export const fetchOrganizationNameUnique = (orgName) =>
  (dispatch, getState) => {
    // Run Unique check when orgName is non-null.
    if (!orgName) { return; }

    const config = getConfig(getState());
    const { portal } = config;
    const queryParam = encodeURIComponent(`'${orgName}'`);
    const url =
      `${portal.hostname}/admin/Portal.svc/OrganizationNameUnique()?Name=${queryParam}`;

    dispatch({
      type: Constants.ORGANIZATION_NAME_UNIQUE_REQUEST,
    });
    debouncedAxiosGet.cancel();
    debouncedAxiosGet(
      url,
      { credentials: 'include' },
      (response) => dispatch({
        type: Constants.ORGANIZATION_NAME_UNIQUE_SUCCESS,
        payload: response.data,
      }),
      () => dispatch({
        type: Constants.ORGANIZATION_NAME_UNIQUE_ERROR,
        payload: [''],
      }),
    );
  };

export const createOrganization = (orgDetails) =>
  async (dispatch, getState) => {
    initApplication(dispatch);
    const config = getConfig(getState());
    const { portal } = config;
    const data = getOrgPayload(orgDetails);
    dispatch({
      type: Constants.ORGANIZATION_CREATE_REQUEST,
    });

    const url = `${portal.hostname}/admin/Portal.svc/Organizations`;
    await axios.post(url, data, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.ORGANIZATION_CREATE_SUCCESS,
          payload: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: Constants.ORGANIZATION_CREATE_ERROR,
          payload: get(error, 'response.data'),
        });
      });
  };

export const updateOrganization = (orgDetails) =>
  async (dispatch, getState) => {
    initApplication(dispatch);
    const config = getConfig(getState());
    const { portal } = config;
    const data = getOrgPayload(orgDetails);
    dispatch({
      type: Constants.ORGANIZATION_UPDATE_REQUEST,
    });

    const url = `${portal.hostname}/admin/Portal.svc/Organizations('${orgDetails.orgUuid}')`;
    await axios.put(url, data, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: Constants.ORGANIZATION_UPDATE_SUCCESS,
          payload: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: Constants.ORGANIZATION_UPDATE_ERROR,
          payload: get(error, 'response.data'),
        });
      });
  };



// WEBPACK FOOTER //
// ./src/actions/organization/index.js